<template>
  <b-row cols="12" class="mx-3 bg-white rounded-sm fundraising-box" no-gutters>
    <b-col
      cols="12"
      v-if="fundraising"
      class="mb-0 text-blue-500 py-2 px-3 bg-mint text-md rounded-sm rounded-bottom-0"
    >
      <p class="mb-0 pr-5" v-orphans>
        {{ fundraisingText }}
      </p>
    </b-col>
    <b-col
      class="border-dotted border-3 rounded-sm p-3"
      :class="{
        'border rounded-top-0 border-top-0 border-mint': fundraising,
        'border-mint-200': !fundraising
      }"
    >
      <b-row
        v-if="fundraising"
        no-gutters
        align-h="between"
        class="text-blue-500 text-md"
        align-v="center"
      >
        <b-col v-if="isDonationOnDonorDataPage" cols="auto">
          {{ $t('childrenList.card.donationCollected') }}
          <span class="text-lg font-weight-bold">{{ currentBalancePLN }} zł</span>
        </b-col>
        <b-col v-else cols="auto">
          <span class="text-lg font-weight-bold">{{ addedValuePLN || currentBalancePLN }} zł</span>
          {{
            showValueDescription ? $t('childrenList.card.add') : $t('childrenList.card.collected')
          }}
        </b-col>
        <b-col cols="auto">
          {{ $t('childrenList.card.goal') }} <span class="text-lg">{{ max }} zł</span>
        </b-col>
        <ProgressBar
          :max="max"
          :currentBalance="currentBalancePLN"
          :add="addedValuePLN"
          :showValue="closed"
        ></ProgressBar>
      </b-row>
      <b-row v-else align-h="center" align-v="center" class="h-100">
        <b-col v-orphans cols="12" align-self="center" class="font-responsive">
          <p v-if="isDonorDataRoute" class="text-center mb-2">
            {{
              isInstalments
                ? $t('form.typeOfSupport.buyInstalmentsIndex')
                : $t('form.typeOfSupport.buyFullIndex')
            }}
          </p>
          <p v-else class="text-danger text-center mb-2">{{ fundraisingText }}</p>
          <p
            class="text-blue-500 text-center px-1 px-md-5 mb-0 text-lg"
            v-html="showValueDescription ? valueDescription : helpToChangeText"
          ></p>
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
import { SOFT_PAYMENT_LIMIT } from '@/constants/payment'
import ProgressBar from '@/components/widgets/childCard/ProgressBar.vue'
import { convertToMajorUnit } from '@/helpers/money'
import { PaymentType } from '@/types'

import { storeToRefs } from 'pinia'
import useTransactionDataStore from '@/stores/transactionData'

export default {
  name: 'MoneyProgressBar',
  components: { ProgressBar },
  props: {
    fundraising: [Object, Boolean],
    firstNameGenitive: String,
    showValueDescription: {
      type: Boolean,
      default: false
    },
    instalmentsValues: {
      default: null
    },
    isInstalments: {
      type: Boolean,
      default: false
    },
    closed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      min: 0,
      max: SOFT_PAYMENT_LIMIT
    }
  },
  computed: {
    fundraisingPaymentsTotalAmount() {
      return this.$store.getters['child/getPaidAmount']
    },
    fundraisingCurrentValue() {
      return this.fundraising && this.fundraising.paid_amount
    },
    currentBalance() {
      return this.fundraisingCurrentValue || this.fundraisingPaymentsTotalAmount
    },
    currentBalancePLN() {
      return convertToMajorUnit(this.currentBalance)
    },
    openFundraisingAddValue() {
      return this.fundraising && this.fundraising.add_value
    },
    fundraisingDonateValue() {
      return this.instalmentsValues && this.instalmentsValues.value
    },
    addedValue() {
      return this.openFundraisingAddValue || this.fundraisingDonateValue || 0
    },
    addedValuePLN() {
      return convertToMajorUnit(this.addedValue)
    },
    helpToChangeText() {
      if (this.firstNameGenitive) {
        return this.$t('childrenList.card.helpToChange', {
          firstNameGenitive: this.firstNameGenitive
        })
      }
      return this.$t('childrenList.card.helpToChange', {
        firstNameGenitive: this.$t('childrenList.card.child')
      })
    },
    valueDescription() {
      if (this.isDonorDataRoute) {
        if (this.instalmentsValues && this.isInstalments) {
          return this.$t('form.typeOfSupport.donorInstalmentsValue', {
            value: convertToMajorUnit(this.instalmentsValues.value)
          })
        }
        return this.$t('form.typeOfSupport.indexValue', { value: 1000 })
      }
      return this.$t('form.typeOfSupport.indexValue', { value: 1000 })
    },
    isDonorDataRoute() {
      return this.$route.name === 'FormOfDonorData'
    },
    isChildPage() {
      return this.$route.name === 'ChildPage' || this.$route.name === 'ChildPageRetention'
    },
    isDonationOnDonorDataPage() {
      return this.currentBalancePLN > 0 && this.fundraising?.add_value && this.isDonorDataRoute
    },
    fundraisingText() {
      if (this.fundraising) {
        if (this.closed) {
          return this.$t('childPage.closedFundraisingMoneyProgressTitle')
        }
        if (this.showValueDescription && this.paymentType === PaymentType.fundraising) {
          return this.$t('form.typeOfSupport.openFundraisingIndex')
        }
        return this.isChildPage ? '' : this.$t('childrenList.card.openFundraising')
      }
      return this.isChildPage ? '' : this.$t('childrenList.card.waitingFundraising')
    }
  },
  setup() {
    const transactionDataStore = useTransactionDataStore()
    const { paymentType } = storeToRefs(transactionDataStore)

    return {
      paymentType
    }
  }
}
</script>
